import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Image, Badge, Tab, Tabs } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { FaChalkboardTeacher, FaBook, FaUserGraduate, FaStar, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { getUserById } from '../../services/userService';
import { getCoursesByTeacherId } from '../../services/courseService';
import { User, Course } from '../../types';
import './TeacherProfilePage.css';

const TeacherProfilePage: React.FC = () => {
  const { teacherId } = useParams<{teacherId: string}>();
  const [teacher, setTeacher] = useState<User | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('courses');

  useEffect(() => {
    const fetchTeacherData = async () => {
      try {
        setLoading(true);
        
        if (!teacherId) {
          setError('معرف المعلم غير صحيح');
          setLoading(false);
          return;
        }
        
        // Fetch teacher data
        const teacherData = await getUserById(teacherId);
        setTeacher(teacherData);
        
        // Fetch teacher's courses
        const teacherCourses = await getCoursesByTeacherId(teacherId);
        setCourses(teacherCourses);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching teacher data:', err);
        setError('حدث خطأ أثناء تحميل بيانات المعلم. يرجى المحاولة مرة أخرى.');
        setLoading(false);
      }
    };
    
    fetchTeacherData();
  }, [teacherId]);

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">جاري تحميل بيانات المعلم...</p>
      </Container>
    );
  }

  if (error || !teacher) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error || 'لم يتم العثور على المعلم'}</Alert>
        <Link to="/teachers">
          <Button variant="primary">
            العودة إلى قائمة المعلمين
          </Button>
        </Link>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row>
        <Col lg={4} md={5} className="mb-4">
          <Card className="teacher-profile-card shadow-sm">
            <Card.Body className="text-center">
              {teacher.photoURL ? (
                <Image 
                  src={teacher.photoURL} 
                  alt={teacher.name} 
                  roundedCircle 
                  className="teacher-profile-image mb-4"
                  style={{ width: '180px', height: '180px', objectFit: 'cover', border: '5px solid #f8f9fa' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = 'https://via.placeholder.com/180?text=معلم';
                  }}
                />
              ) : (
                <div className="teacher-profile-placeholder mb-4 mx-auto">
                  <FaChalkboardTeacher size={80} className="text-primary" />
                </div>
              )}
              
              <h3 className="mb-1">{teacher.name}</h3>
              <p className="text-muted mb-3">{teacher.specialization || 'معلم'}</p>
              
              {teacher.isVerified && (
                <Badge bg="success" className="mb-3 px-3 py-2">معلم معتمد</Badge>
              )}
              
              <div className="d-flex justify-content-around my-4">
                <div>
                  <FaBook className="text-primary mb-2" size={20} />
                  <p className="mb-0">الدورات</p>
                  <h5>{courses.length}</h5>
                </div>
                <div>
                  <FaUserGraduate className="text-success mb-2" size={20} />
                  <p className="mb-0">الطلاب</p>
                  <h5>0</h5>
                </div>
                <div>
                  <FaStar className="text-warning mb-2" size={20} />
                  <p className="mb-0">التقييم</p>
                  <h5>5.0</h5>
                </div>
              </div>
              
              <hr />
              
              {teacher.phoneNumber && (
                <div className="d-flex align-items-center mb-3">
                  <FaPhone className="text-primary me-3" />
                  <div className="text-start">
                    <p className="mb-0">{teacher.phoneNumber}</p>
                  </div>
                </div>
              )}
              
              {teacher.email && (
                <div className="d-flex align-items-center mb-3">
                  <FaEnvelope className="text-primary me-3" />
                  <div className="text-start">
                    <p className="mb-0">{teacher.email}</p>
                  </div>
                </div>
              )}
              
              {teacher.address && (
                <div className="d-flex align-items-center mb-3">
                  <FaMapMarkerAlt className="text-primary me-3" />
                  <div className="text-start">
                    <p className="mb-0">{teacher.address}</p>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={8} md={7}>
          <Card className="shadow-sm mb-4">
            <Card.Body>
              <h4 className="mb-3">نبذة عن المعلم</h4>
              <p>{teacher.bio || 'لا توجد معلومات متاحة عن هذا المعلم.'}</p>
            </Card.Body>
          </Card>
          
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || 'courses')}
            className="mb-4"
          >
            <Tab eventKey="courses" title="الدورات">
              {courses.length === 0 ? (
                <Alert variant="info">
                  لا توجد دورات متاحة لهذا المعلم حالياً
                </Alert>
              ) : (
                <Row>
                  {courses.map((course) => (
                    <Col key={course.id} md={6} className="mb-4">
                      <Card className="h-100 shadow-sm course-card">
                        <div style={{ height: '160px', overflow: 'hidden' }}>
                          <Card.Img 
                            variant="top" 
                            src={course.imageUrl || 'https://via.placeholder.com/300x160?text=Course'} 
                            alt={course.title}
                            style={{ height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = 'https://via.placeholder.com/300x160?text=Course';
                            }}
                          />
                        </div>
                        <Card.Body className="d-flex flex-column">
                          <Card.Title>{course.title}</Card.Title>
                          <Card.Text>
                            {course.description && course.description.length > 100 
                              ? `${course.description.substring(0, 100)}...` 
                              : course.description}
                          </Card.Text>
                          <div className="d-flex justify-content-between mt-auto">
                            <Badge bg="primary">{course.subject || 'عام'}</Badge>
                            <Badge bg="info">{course.level || 'جميع المستويات'}</Badge>
                          </div>
                          <Link to={`/courses/${course.id}`}>
                            <Button variant="outline-primary" className="w-100 mt-3">
                              عرض الدورة
                            </Button>
                          </Link>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Tab>
            <Tab eventKey="reviews" title="التقييمات">
              <Alert variant="info">
                لا توجد تقييمات متاحة لهذا المعلم حالياً
              </Alert>
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </Container>
  );
};

export default TeacherProfilePage;
